import { useState } from "react";

import { SubmitAction } from "../../action/Action";
import { PromiseToast } from "../../components/async/PromiseToast";
import Button from "../../components/Button/Button";
import Centered from "../../components/Centered";
import Form from "../../components/form/Form";
import type { TextFieldProps } from "../../components/form/TextField";
import TextField from "../../components/form/TextField";
import useField from "../../components/form/useField";
import { validationFn } from "../../components/form/utilities";
import Grid from "../../components/Grid";
import Spacer from "../../components/space/Spacer";
import Text from "../../components/Text";
import { callOutgoingRequest } from "../../data/tanstack-query";
import { confirmEmailCode, requestEmailCode } from "../../endpoints/outgoing-requests";
import useSubmit from "../../hooks/useSubmit";

export default function EmailAuthScreen() {
    const [email, setEmail] = useState<string | null>(null);
    if (email === null) return <EmailStep onSent={setEmail} />;
    return <CodeStep email={email} onChangeEmail={() => setEmail(null)} />;
}

function EmailStep(props: { onSent: (email: string) => void }) {
    const emailField = useField<TextFieldProps>({
        value: undefined,
        label: "Adresse email",
        type: "email",
        required: true,
    });
    const [onSubmit, submission] = useSubmit(async () => {
        const email = emailField.validate().trim();
        await callOutgoingRequest(requestEmailCode({ email }));
        props.onSent(email);
    }, []);
    return (
        <>
            <PromiseToast promise={submission} message="Code envoyé" />
            <Text variant="body">
                Indiquez votre adresse email. Un code à 6 chiffres vous sera envoyé.
            </Text>
            <Spacer scale={4} />
            <Form onSubmit={onSubmit}>
                <Grid columns={1} gap={4}>
                    <TextField {...emailField.props} />
                    <Centered>
                        <Button label="Recevoir un code" action={SubmitAction} />
                    </Centered>
                </Grid>
            </Form>
        </>
    );
}

function CodeStep(props: { email: string; onChangeEmail: () => void }) {
    const codeField = useField<TextFieldProps>({
        value: undefined,
        label: "Code",
        required: true,
        validator: validationFn("code", (value) => {
            if (!/^\d{6}$/.test(value.trim())) throw new Error("Saisissez les 6 chiffres");
        }),
    });
    const [onSubmit, submission] = useSubmit(async () => {
        const code = codeField.validate().trim();
        await callOutgoingRequest(confirmEmailCode({ email: props.email, code }));
    }, [props.email]);
    const [onResend, resend] = useSubmit(async () => {
        await callOutgoingRequest(requestEmailCode({ email: props.email }));
    }, [props.email]);
    return (
        <>
            <PromiseToast promise={submission} message="Connexion réussie" />
            <PromiseToast promise={resend} message="Code renvoyé" />
            <Text variant="body">Saisissez le code envoyé à {props.email}.</Text>
            <Spacer scale={4} />
            <Form onSubmit={onSubmit}>
                <Grid columns={1} gap={4}>
                    <TextField {...codeField.props} />
                    <Centered>
                        <Button label="Continuer" action={SubmitAction} />
                    </Centered>
                    <Centered>
                        <Button
                            label="Renvoyer le code"
                            action={onResend}
                            feeling="minor"
                            role="minor"
                        />
                    </Centered>
                    <Centered>
                        <Button
                            label="Changer d'email"
                            action={props.onChangeEmail}
                            feeling="minor"
                            role="minor"
                        />
                    </Centered>
                </Grid>
            </Form>
        </>
    );
}
